// Test drive helpers (pure, unit-tested). Test drive plays a campaign's signup
// flow end to end: the real form (test mode) on a stand-in website, the signup
// email arriving in a simulated inbox, the confirm click and what follows —
// with nothing saved. The backend side is the test session endpoints
// (/c/<org>/test/<session>/...).
//
// (Named driveHelpers, not testDrive: macOS filesystems are case-insensitive,
// so testDrive.js and TestDrive.js would be the same file.)

const esc = (s) =>
  String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// testBase is the root of a session's test endpoints.
export function testBase(publicBaseUrl, orgHash, sessionId) {
  return `${(publicBaseUrl || "").replace(/\/+$/, "")}/c/${orgHash}/test/${sessionId}`;
}

// formScriptUrl is the test-mode form script for the stand-in page.
export function formScriptUrl(base, formId) {
  return `${base}/form${formId ? `?variant=${encodeURIComponent(formId)}` : ""}`;
}

// standInPage is the generic website the form is shown on: a plain header,
// placeholder copy and a footer around the campaign's real form (its embed
// script, in test mode).
export function standInPage(scriptUrl) {
  const bar = (w, h = 10) => `<div class="bar" style="width:${w}%;height:${h}px"></div>`;
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<style>
*{box-sizing:border-box} body{margin:0;font-family:-apple-system,"Helvetica Neue",Helvetica,Arial,sans-serif;background:#fff;color:#1d2129}
header{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:14px 24px;border-bottom:1px solid #eceef2}
.logo{display:flex;align-items:center;gap:8px;font-weight:700;color:#4e5969}.dot{width:22px;height:22px;border-radius:6px;background:#c9cdd4}
nav{display:flex;gap:14px}nav span{width:54px;height:9px;border-radius:5px;background:#e5e6eb}
main{max-width:980px;margin:0 auto;padding:32px 24px}
.bar{background:#eceef2;border-radius:6px;margin:0 0 10px}
.test{position:sticky;top:0;z-index:5;padding:7px 14px;text-align:center;font-size:12px;font-weight:700;letter-spacing:.02em;color:#3d2a00;background:repeating-linear-gradient(135deg,#ffd666 0 14px,#ffe58f 14px 28px);border-bottom:2px solid #d4a106}
.hero{margin-bottom:28px}.hero .bar:first-child{background:#dfe2e7}
.form-slot{margin:8px 0 32px}
.cols{display:flex;flex-wrap:wrap;gap:20px}.cols>div{flex:1 1 220px}
footer{margin-top:24px;padding:22px 24px;border-top:1px solid #eceef2;color:#86909c;font-size:12px}
@media (max-width:600px){nav{display:none}main{padding:20px 16px}}
</style></head><body>
<div class="test">TEST · A stand-in page for your site: nothing you do here is saved</div>
<header><div class="logo"><div class="dot"></div>Your website</div><nav><span></span><span></span><span></span></nav></header>
<main>
  <div class="hero">${bar(70, 26)}${bar(52, 26)}${bar(90)}${bar(80)}</div>
  <div class="form-slot"><script src="${esc(scriptUrl)}"></script></div>
  <div class="cols"><div>${bar(95)}${bar(88)}${bar(60)}</div><div>${bar(92)}${bar(84)}${bar(70)}</div></div>
</main>
<footer>A stand-in page: your form looks and works as it will on your site, but nothing is saved.</footer>
</body></html>`;
}

// driveEmailHtml readies a rendered email for the simulated inbox. First it's
// cleaned the way mail apps clean what they show — no scripts, embedded
// frames/objects, event handlers or javascript: links — so the inbox can show
// it without a sandbox. Then the links a subscriber's click would take
// somewhere open in Test drive's "Their browser" pane (the frame named
// BROWSER_FRAME), in drive mode: links into the session (confirm, unsubscribe,
// tracked links; they report to the log, then go where a subscriber would) and
// the reward button (via the session's /arrive, which opens it — spending a
// use, as their click would — and reports the address). Any other link opens
// in a new tab.
export const BROWSER_FRAME = "jc-browser";

export function driveEmailHtml(html, base) {
  const rewardPrefix = base ? base.slice(0, base.lastIndexOf("/test/")) + "/incentive/" : "";
  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
  doc.querySelectorAll("script, iframe, frame, object, embed, base, meta[http-equiv]").forEach((n) => n.remove());
  doc.querySelectorAll("*").forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on")) el.removeAttribute(attr.name);
      if ((name === "href" || name === "src" || name === "action") && /^\s*javascript:/i.test(attr.value)) el.removeAttribute(attr.name);
    });
  });
  doc.querySelectorAll("a[href]").forEach((a) => {
    const href = a.getAttribute("href");
    a.removeAttribute("rel");
    if (base && href.startsWith(base + "/")) {
      a.setAttribute("href", href + (href.includes("?") ? "&" : "?") + "drive=1");
      a.setAttribute("target", BROWSER_FRAME);
    } else if (rewardPrefix && href.startsWith(rewardPrefix)) {
      a.setAttribute("href", arriveUrl(base, href));
      a.setAttribute("target", BROWSER_FRAME);
    } else {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener");
    }
  });
  return "<!doctype html>" + doc.documentElement.outerHTML;
}

// arriveUrl opens a reward link in the browser pane via the session's /arrive
// (which reports the address it lands on).
export function arriveUrl(base, rewardLink) {
  const i = rewardLink.lastIndexOf("/incentive/");
  const token = rewardLink.slice(i + "/incentive/".length).split(/[?#]/)[0];
  return `${base}/arrive?reward=${encodeURIComponent(token)}`;
}

// confirmSteps turns a (simulated) confirm click into activity-log lines —
// what happens, plus a "tip" (with where to fix it) where the moment is
// awkward for the subscriber and worth smoothing over.
export function confirmSteps(data) {
  const d = data || {};
  const label = d.reward_label || "their reward";
  const steps = [{ kind: "done", text: "They clicked Confirm: they're subscribed now (pending lists become active)." }];
  if (d.kind === "reward") {
    steps.push({ kind: "done", text: `Their browser goes straight to “${label}”: it opens (or downloads) right here, as it will for them.` });
    steps.push({ kind: "tip", fix: "settings", fix_field: "reward_delivery", text: "There's no “you're subscribed” moment: the reward just opens. Consider “see “You're confirmed!”, then their reward opens” (under After they click Confirm)." });
  } else if (d.kind === "page") {
    steps.push({ kind: "done", text: `They're sent to your confirmation page: ${d.url}` });
  } else if (d.reward_page) {
    steps.push({ kind: "done", text: `They see “You're confirmed!”, then “${label}” opens by itself (with a button in case it doesn't).` });
  } else if (d.followup) {
    steps.push({ kind: "done", text: "They see “You're confirmed!”, and the follow-up email with their reward is sent." });
    steps.push({ kind: "tip", fix: "settings", fix_field: "reward_delivery", text: "They have to go back to their inbox for the reward. Consider handing it over right away: “see “You're confirmed!”, then their reward opens” (under After they click Confirm)." });
  } else {
    steps.push({ kind: "done", text: "They see the built-in “You're confirmed!” page, and that's where it ends." });
    steps.push({ kind: "tip", fix: "settings", fix_field: "confirmed_url", text: "It's a plain page with nowhere to go next. Consider sending them to your own welcome page (“go to your page”, under After they click Confirm)." });
  }
  return steps;
}

// ---- keeping a Test drive across closes, visits and reloads ----
// The panel's state (session, log, inbox, choices) is kept per campaign in
// sessionStorage (this browser tab), so fixing something and coming back picks
// up where it left off. The stand-in form's own state (e.g. its thank-you) is
// in localStorage under jc_test_<session>_*, so it survives too.

const driveKey = (campaignId) => `jc.testdrive.${campaignId}`;

export function saveDriveState(storage, campaignId, state) {
  try {
    storage.setItem(driveKey(campaignId), JSON.stringify(state));
  } catch (e) {
    // storage full/blocked: the drive just won't survive a reload
  }
}

// loadDriveState returns the saved drive, or null (none, unreadable, or its
// session has expired).
export function loadDriveState(storage, campaignId, now = Date.now()) {
  try {
    const state = JSON.parse(storage.getItem(driveKey(campaignId)) || "null");
    if (!state || !state.drive || !state.drive.session_id) return null;
    if (new Date(state.drive.expires_at).getTime() <= now) return null;
    return state;
  } catch (e) {
    return null;
  }
}

export function clearDriveState(storage, campaignId) {
  try {
    storage.removeItem(driveKey(campaignId));
  } catch (e) {
    // nothing to clear
  }
}

// clearFormState forgets the stand-in form's browser state for a session, so
// it loads fresh, as for a new visitor.
export function clearFormState(storage, sessionId) {
  try {
    const prefix = `jc_test_${sessionId}_`;
    const keys = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
    keys.forEach((k) => storage.removeItem(k));
  } catch (e) {
    // storage blocked: nothing to clear
  }
}

// ---- the reward link ----
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// confirmLinkIn finds the confirm link in a (drive-ready, see driveEmailHtml)
// email, so Test drive can click Confirm for them.
export function confirmLinkIn(html, base) {
  if (!base) return "";
  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
  const a = Array.from(doc.querySelectorAll("a[href]")).find((el) => el.getAttribute("href").startsWith(`${base}/confirm`));
  return a ? a.getAttribute("href") : "";
}

// rewardLinkIn finds the reward link (/c/<org>/incentive/<token>) in an email.
export function rewardLinkIn(html, orgHash) {
  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
  const a = Array.from(doc.querySelectorAll("a[href]")).find((el) => el.getAttribute("href").includes(`/c/${orgHash}/incentive/`));
  return a ? a.getAttribute("href") : "";
}

// theirRewardLink is the reward link the subscriber ends up holding: the one
// in the follow-up email, else the one the confirm click handed over, else the
// one in the welcome email (single opt-in). emails are newest first.
export function theirRewardLink(emails, confirmRewardUrl, orgHash) {
  const list = emails || [];
  const followup = list.find((m) => m.stage === "followup");
  if (followup) return rewardLinkIn(followup.html, orgHash);
  if (confirmRewardUrl) return confirmRewardUrl;
  const withReward = list.find((m) => rewardLinkIn(m.html, orgHash));
  return withReward ? rewardLinkIn(withReward.html, orgHash) : "";
}

// runOutSteps are the log lines once the reward link has run out (how "time"
// or "uses").
export function runOutSteps(how, limits) {
  const l = limits || {};
  return [
    how === "uses"
      ? { kind: "done", text: `Their reward link has been used ${plural(l.uses, "time", "times")}: all its ${l.link ? "clicks" : "downloads"} are used up.` }
      : { kind: "done", text: `Jumped ahead ${plural(l.expiry_hours, "hour", "hours")}: their reward link has expired.` },
    { kind: "info", text: "Opening it now shows “This link has run out” (in their browser), and that's where it ends." },
    { kind: "tip", fix: "settings", fix_field: "reward_limits", text: `Anyone who comes back for it later hits a dead end. If that's not what you want, allow more time or ${l.link ? "clicks" : "downloads"}, or switch on Unlimited.` },
  ];
}

// ---- moments ----
// Test drive records each moment of the flow (the page loading, subscribing,
// an email arriving, opening it, each click) with a snapshot of both panes,
// so any moment can be gone back to as it was.

// readFormState / writeFormState: the stand-in form's own saved state (its
// thank-you, etc., under jc_test_<session>_*), part of a moment's snapshot.
export function readFormState(storage, sessionId) {
  const out = {};
  try {
    const prefix = `jc_test_${sessionId}_`;
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith(prefix)) out[k.slice(prefix.length)] = storage.getItem(k);
    }
  } catch (e) {
    // storage blocked: nothing saved
  }
  return out;
}

export function writeFormState(storage, sessionId, state) {
  clearFormState(storage, sessionId);
  try {
    Object.entries(state || {}).forEach(([k, v]) => storage.setItem(`jc_test_${sessionId}_${k}`, v));
  } catch (e) {
    // storage blocked
  }
}

// absoluteSrc: a server path (/c/...) on the API's public origin.
export function absoluteSrc(publicBaseUrl, src) {
  return src && src.startsWith("/") ? `${(publicBaseUrl || "").replace(/\/+$/, "")}${src}` : src || "";
}

// An email's subject as an inbox shows it: "(no subject)" when there's none.
export const shownSubject = (m) => ((m && m.subject) || "").trim() || "(no subject)";

// quotedSubject names an email in a moment's title: “Subject”, or (without one)
// the email with no subject — never empty quotes.
export const quotedSubject = (m, article = "the") => {
  const s = ((m && m.subject) || "").trim();
  return s ? `“${s}”` : `${article} email with no subject`;
};

// ---- paths ----
// The moments, kept as a tree of one path: going back to an earlier moment
// pops everything after it (popTo).
// tree = { nodes: { id: moment + { parent, seq } }, leaf: id of the current
// path's last moment, seq: a counter (the newest branch wins ties) }.

export const emptyTree = () => ({ nodes: {}, leaf: null, seq: 0 });

// pathOf: the current path's moments, first to last.
export function pathOf(tree) {
  const out = [];
  let id = tree && tree.leaf;
  while (id && tree.nodes[id]) {
    out.unshift(tree.nodes[id]);
    id = tree.nodes[id].parent;
  }
  return out;
}

// addNode adds a moment after the current path's last one, and moves there.
export function addNode(tree, node) {
  const seq = tree.seq + 1;
  return { nodes: { ...tree.nodes, [node.id]: { ...node, parent: tree.leaf, seq } }, leaf: node.id, seq };
}

// updateNode changes a moment in place.
export function updateNode(tree, id, change) {
  if (!tree.nodes[id]) return tree;
  return { ...tree, nodes: { ...tree.nodes, [id]: { ...tree.nodes[id], ...change } } };
}

// treeFromMoments makes a tree of a list of moments (one path).
export function treeFromMoments(moments) {
  return (moments || []).reduce((t, m) => addNode(t, m), emptyTree());
}

// ---- the flow graph ----
// flowGraph is every way through the campaign's signup flow, from its
// settings: a step for each screen they pass through (parent = the step
// before), branching where they have a choice — each button in the email
// (its Confirm button, the reward button, a tracked link, Unsubscribe), their
// reward link running out (time or downloads) before they come back, never
// confirming, or coming back to your site. A file reward downloads (their
// browser stays where it was); a link reward opens. labels (optional) are the
// buttons' own words, from the email once it has arrived: { confirm, reward,
// links: { name: words } }. blocked (optional) is why the signup email can't
// go out ({ text, fix, fix_field }): its step shows that, and every step after
// it can't be reached.
export function flowGraph(drive, labels = {}, blocked = null) {
  const d = drive || {};
  const nodes = [];
  // why: which settings put the step there (the graph's hover explains it);
  // fix: where to change them ({ fix, fix_field }, as the log's fix buttons).
  const add = (key, label, parent, desc = "", why = "", fix = null, muted = false) => nodes.push({ key, label, parent, desc, why, fix, muted });
  // caution: it works, but isn't set up yet (e.g. the default wording).
  const caution = (key, text) => Object.assign(nodes.find((n) => n.key === key), { caution: text });
  const double = !!d.double_opt_in;
  // A follow-up email only follows a double opt-in confirm; otherwise the
  // confirm click hands the reward straight over.
  let delivery = d.incentive_delivery || "redirect";
  if (!double && delivery === "email") delivery = "redirect";
  // A reward needs its file or link set (the server sends its limits then).
  const reward = !!d.include_incentive && !!d.reward_limits;
  const l = d.reward_limits || {};
  const isLink = !!l.link;
  const thing = isLink ? "link" : "file";
  const clickConfirm = labels.confirm ? `Click “${labels.confirm}”` : "Click Confirm";
  const clickReward = labels.reward ? `Click “${labels.reward}”` : "Click the reward";
  const liveForms = (d.forms || []).filter((f) => f.should_run).length;
  const liveEmails = d.live_signup_emails || 0;
  const setting = (field) => ({ fix: "settings", fix_field: field });
  const limitsWhy = l.unlimited
    ? "Their link has no limits (Unlimited): it works any number of times and never expires."
    : `Each person's link works for ${plural(l.uses, isLink ? "click" : "download", isLink ? "clicks" : "downloads")} within ${plural(l.expiry_hours, "hour", "hours")} (the reward's Limits).`;
  const afterConfirmWhy = () => {
    if (reward && delivery === "redirect") return `“After they click Confirm, they… go straight to their reward”: your reward is a ${thing}, so it ${isLink ? "opens" : "downloads"} right away.`;
    if (reward && delivery === "page") return "“After they click Confirm, they… see “You're confirmed!”, then their reward opens”.";
    if (reward && delivery === "email") return `“After they click Confirm, they… ${d.confirm_email_url ? `go to your page (${d.confirm_email_url})` : "see “You're confirmed!”"}, and get their reward in a follow-up email”.`;
    return d.confirm_email_url
      ? `“After they click Confirm, they… go to your page”: ${d.confirm_email_url}.`
      : "“After they click Confirm, they… see “You're confirmed!”” (the built-in page).";
  };

  add("visit", "Visit your site", null, "Your form, on a stand-in page",
    liveForms > 1
      ? `Your web form, on a stand-in for your site. You have ${liveForms} live forms: each visitor gets one of them at random (an A/B test).`
      : "Your web form, on a stand-in for your site: it looks and works as it will there.",
    { fix: "forms", fix_field: "forms" });
  add("subscribe", "Subscribe", "visit", d.form_thank_you_url ? "→ your thank-you page" : "They fill in the form",
    d.form_thank_you_url
      ? `They fill in the form and press its button. On your site, they're then sent to your Form Thank You URL (${d.form_thank_you_url}); Test drive stays on the form.`
      : "They fill in the form and press its button, and the form shows its thank-you message (set in the form's “After signing up”).",
    d.form_thank_you_url ? setting("thank_you_url") : { fix: "forms", fix_field: "forms" });
  add("email", double ? "Confirmation email arrives" : "Welcome email arrives", "subscribe",
    double ? "Asking them to confirm" : reward ? "With their reward" : "",
    (double
      ? "Require Confirmation is on: the signup email asks them to confirm, and they're not subscribed until they do."
      : "Require Confirmation is off: they're subscribed straight away, and the signup email welcomes them.") +
      (d.signup_email_source === "custom"
        ? (liveEmails > 1 ? ` It's your own design: you have ${liveEmails} live ones, and each person gets one of them at random (an A/B test).` : " It's your own design.")
        : ` It's the built-in email: your wording${double ? ", with a Confirm button added" : ""}.`),
    setting("double_opt_in"));
  add("open", "Open it", "email", "", "They open the email in their inbox.", { fix: "signup_email", fix_field: "signup_emails" });

  let holder = null; // the step where they hold their reward link in an email
  if (double || d.signup_email_confirms || labels.confirm) {
    let after = d.confirm_email_url ? "→ your page" : "→ “You're confirmed!”";
    if (reward && delivery === "redirect") after = isLink ? "→ their reward" : "→ their download";
    if (reward && delivery === "page") after = "→ “You're confirmed!”";
    add("confirm", clickConfirm, "open", after,
      (double
        ? "The signup email's Confirm button: clicking it subscribes them. "
        : "Your signup email has a Confirm button, though Require Confirmation is off (they're subscribed already). ") + afterConfirmWhy(),
      reward ? setting("reward_delivery") : double ? setting("confirmed_url") : { fix: "signup_email", fix_field: "signup_emails" });
    if (reward && delivery === "page") {
      add("rewardOpens", isLink ? "Their reward opens" : "Their download starts", "confirm", "By itself, a moment later",
        `“After they click Confirm, they… see “You're confirmed!”, then their reward opens”: a moment after the page shows, their reward ${isLink ? "opens" : "downloads (their browser stays on the page)"}, with a button in case it doesn't.`,
        setting("reward_delivery"));
    }
    if (double && reward && delivery === "email") {
      const followupWhy = "“…and get their reward in a follow-up email”: once they confirm, the follow-up email (under Emails) brings it.";
      const designs = d.followup_designs || 0;
      if (d.followup_email_source === "custom") {
        add("followup", "Follow-up email arrives", "confirm", designs > 1 ? `Your ${designs} designs (A/B)` : "Your design",
          `${followupWhy} It's your own design${designs > 1 ? `: each person gets one of your ${designs} at random (an A/B test)` : ""}, with the reward's button added below.`,
          { fix: "signup_email", fix_field: "followup" });
      } else if (l.followup_default) {
        add("followup", "Follow-up email arrives", "confirm", "The default email",
          `${followupWhy} You haven't written it yet, so the default goes: “${l.followup_subject}”, “${l.followup_body}”, and the button.`,
          setting("followup"));
        caution("followup", "Uses the default wording: write your own under Emails → Follow-up email.");
      } else {
        add("followup", "Follow-up email arrives", "confirm", l.followup_subject ? `“${l.followup_subject}”` : "With their reward", followupWhy, setting("followup"));
      }
      add("openFollowup", "Open it", "followup", "", "They open the follow-up email.", setting("followup"));
      holder = "openFollowup";
    }
  }
  // Single opt-in: the welcome email carries the reward button.
  if (!double && reward) holder = "open";
  if (holder) {
    const where = holder === "open"
      ? "The campaign gives a reward and doesn't ask people to confirm, so the reward button is added below your welcome email's text automatically."
      : "The follow-up email's reward button.";
    add("reward", clickReward, holder, isLink ? "Opens it" : "Downloads it", `${where} ${limitsWhy}`, setting("reward"));
    if (l.unlimited) {
      add("unlimited", "Never runs out", holder, "Unlimited is on",
        "The reward's Limits are set to Unlimited: their link works any number of times and never expires, so it can't run out. Switch Unlimited off to try limits here.",
        setting("reward_limits"), true);
    } else {
      add("time", `${plural(l.expiry_hours, "hour", "hours")} pass`, holder, "Then they click it",
        `Their link expires ${plural(l.expiry_hours, "hour", "hours")} after it's sent (the reward's Limits). Coming back later, they'd hit “This link has run out”.`,
        setting("reward_limits"));
      add("uses", `Used ${plural(l.uses, "time", "times")}`, holder, "Then they click it again",
        `Their link works for ${plural(l.uses, isLink ? "click" : "download", isLink ? "clicks" : "downloads")} (the reward's Limits). After that, they'd hit “This link has run out”.`,
        setting("reward_limits"));
    }
  }

  // The other ways things can go (after the main path, so it reads first).
  add("revisit", "Come back to your site", "subscribe", "The form remembers them",
    "Your form remembers who signed up in this browser: coming back, they see its thank-you state (“After signing up”) instead of the form again.",
    { fix: "forms", fix_field: "forms" });
  if (double) {
    add("noConfirm", "Never confirms", "email", "Stays pending",
      "Require Confirmation is on: until they click Confirm, they're pending on the campaign's lists (not subscribed), and nothing more is sent to them.",
      setting("double_opt_in"));
  }
  const linkWords = labels.links || {};
  const linkNames = [...(d.tracked_links || []).map((t) => t.name), ...Object.keys(linkWords)].filter((n, i, all) => all.indexOf(n) === i);
  linkNames.forEach((name) => {
    const t = (d.tracked_links || []).find((x) => x.name === name) || {};
    let host = "";
    try {
      host = t.destination ? new URL(t.destination).host : "";
    } catch (e) {
      host = t.destination || "";
    }
    add(`link:${name}`, `Click “${linkWords[name] || name}”`, "open", host ? `→ ${host}` : "→ not set up",
      `A tracked link in your signup email (“${name}”)${t.destination ? `, going to ${t.destination}` : ", which isn't set up on the campaign"}.` +
        (double ? ` Clicking it confirms them too, but without the reward${reward ? ": only the Confirm button hands that over" : ""}.` : ""),
      { fix: "signup_email", fix_field: "signup_emails" });
  });
  const unsubWhy = "Every email has an Unsubscribe link in its footer (added automatically). " +
    (d.unsubscribe_page ? `It takes them to your organization's unsubscribe page (${d.unsubscribe_page}).` : "They see the built-in “You're unsubscribed” page.");
  const unsubDesc = d.unsubscribe_page ? "→ your unsubscribe page" : "→ “You're unsubscribed”";
  add("unsub", "Click Unsubscribe", "open", unsubDesc, unsubWhy);
  if (nodes.some((n) => n.key === "openFollowup")) add("unsubFollowup", "Click Unsubscribe", "openFollowup", unsubDesc, unsubWhy);

  // A step that can't happen blocks everything after it.
  const block = (key, text, short, fix) => {
    const step = nodes.find((n) => n.key === key);
    if (!step || step.blocked) return;
    Object.assign(step, { desc: short, why: text, fix, blocked: text });
    const after = new Set([key]);
    nodes.forEach((n) => {
      if (after.has(n.parent)) {
        after.add(n.key);
        if (!n.unreachable) n.unreachable = text;
      }
    });
  };
  // The form: without one there's nothing to sign up with; with them all
  // paused, visitors to your site see none (Test drive shows the first).
  const forms = d.forms || [];
  if (d.forms && !forms.length) {
    block("visit", "This campaign has no web form yet, so there's nothing to sign up with. Add one under Web Forms.", "No web form yet",
      { fix: "forms", fix_field: "forms" });
  } else if (forms.length && !liveForms) {
    const visit = nodes.find((n) => n.key === "visit");
    Object.assign(visit, { desc: "All forms paused" });
    caution("visit", "All your web forms are paused, so visitors to your site see no form. Switch one on under Web Forms (Test drive shows your first one anyway).");
  }
  if (blocked) block("email", blocked.text, blocked.short || "Can't be sent", { fix: blocked.fix, fix_field: blocked.fix_field });
  // The follow-up set to your own design, with none made: it can't be sent.
  if (nodes.some((n) => n.key === "followup") && d.followup_email_source === "custom" && !(d.followup_designs || 0)) {
    block("followup", "The follow-up email is set to your own design, but there isn't one yet, so it can't be sent. Make one under Emails, or switch it to the built-in email.",
      "No design yet", { fix: "signup_email", fix_field: "followup" });
  }
  return nodes;
}

// buttonLabels reads the email's buttons' own words (for the graph): its
// Confirm button (a link to the session's confirm) and its reward button.
export function buttonLabels(emails, base, orgHash) {
  const text = (html, match) => {
    const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
    const a = Array.from(doc.querySelectorAll("a[href]")).find((el) => match(el.getAttribute("href")));
    return a ? (a.textContent || "").replace(/\s+/g, " ").trim() : "";
  };
  const out = { links: {} };
  (emails || []).forEach((m) => {
    if (!out.confirm && base) out.confirm = text(m.html, (h) => h.startsWith(`${base}/confirm`));
    if (!out.reward) out.reward = text(m.html, (h) => h.includes(`/c/${orgHash}/incentive/`));
    if (!base) return;
    const doc = new DOMParser().parseFromString(String(m.html || ""), "text/html");
    doc.querySelectorAll("a[href]").forEach((a) => {
      const href = a.getAttribute("href");
      if (!href.startsWith(`${base}/click/`)) return;
      const name = href.slice(`${base}/click/`.length).split(/[?#/]/)[0];
      if (name && !out.links[name]) out.links[name] = (a.textContent || "").replace(/\s+/g, " ").trim() || name;
    });
  });
  if (!Object.keys(out.links).length) delete out.links;
  if (!out.confirm) delete out.confirm;
  if (!out.reward) delete out.reward;
  return out;
}

// graphStatus: how each step of the graph stands, given the moments tried
// (the tree) and the current path — "current", "done" (on this path),
// "elsewhere" (a moment off the current path; none once popped) or "" (not
// yet).
export function graphStatus(graph, tree) {
  const path = pathOf(tree);
  const onPath = new Set(path.map((m) => m.node).filter(Boolean));
  const anywhere = new Set(Object.values(tree.nodes).map((m) => m.node).filter(Boolean));
  const current = currentNode(graph, path);
  const out = {};
  graph.forEach((n) => {
    out[n.key] = n.key === current ? "current" : onPath.has(n.key) ? "done" : anywhere.has(n.key) ? "elsewhere" : "";
  });
  return out;
}

// currentNode: the step the current path has reached (its last moment on the
// graph).
export function currentNode(graph, path) {
  const keys = new Set(graph.map((n) => n.key));
  for (let i = path.length - 1; i >= 0; i--) if (path[i].node && keys.has(path[i].node)) return path[i].node;
  return null;
}

// momentFor: the moment to go back to for a step: the first on the path
// (where the flow went through it; later ones are revisits), or null.
export function momentFor(tree, key) {
  return pathOf(tree).find((m) => m.node === key) || null;
}

// popTo goes back to a moment like popping a stack: everything after it goes,
// so it's as it was when they first got there, and what they do next is new.
export function popTo(tree, id) {
  const keep = pathOf({ ...tree, leaf: id });
  const nodes = {};
  keep.forEach((m) => { nodes[m.id] = m; });
  return { ...tree, nodes, leaf: keep.length ? id : tree.leaf };
}

// ---- driving the form from the graph ----

// sampleSignup is who "Subscribe" in the graph signs up as: a fresh address
// each time (so it's a new signup, not someone already on the list).
export function sampleSignup(now = Date.now()) {
  return { email: `jane.test+${now.toString(36).slice(-6)}@example.com`, name: "Jane Test" };
}

// fillForm fills in the stand-in page's form (doc is its document) as a
// visitor would — their email and name, and anything else it requires — and
// presses its button. The bot trap (website) is left empty. Returns whether
// there was a form to fill.
export function fillForm(doc, who) {
  const form = doc && doc.querySelector("form.subscribe_form");
  if (!form) return false;
  const win = doc.defaultView || window;
  const set = (el, value) => {
    el.value = value;
    el.dispatchEvent(new win.Event("input", { bubbles: true }));
    el.dispatchEvent(new win.Event("change", { bubbles: true }));
  };
  const fields = Array.from(form.querySelectorAll("input, select, textarea"));
  const radiosDone = new Set();
  fields.forEach((el) => {
    const name = (el.getAttribute("name") || "").toLowerCase();
    const type = (el.getAttribute("type") || el.tagName).toLowerCase();
    if (name === "website" || ["hidden", "submit", "button"].includes(type)) return;
    if (name === "email" || type === "email") return set(el, who.email);
    if (name === "name") return set(el, who.name);
    if (!el.required) return;
    if (type === "checkbox") {
      el.checked = true;
      el.dispatchEvent(new win.Event("change", { bubbles: true }));
    } else if (type === "radio") {
      if (!radiosDone.has(name)) {
        radiosDone.add(name);
        el.checked = true;
        el.dispatchEvent(new win.Event("change", { bubbles: true }));
      }
    } else if (el.tagName === "SELECT") {
      const opt = Array.from(el.options).find((o) => o.value);
      if (opt) set(el, opt.value);
    } else if (!el.value) {
      set(el, type === "number" ? "1" : type === "date" ? "2000-01-01" : type === "tel" ? "555-0100" : type === "url" ? "https://example.com" : "Test");
    }
  });
  const button = form.querySelector("button[type=submit], input[type=submit], button:not([type])");
  if (typeof form.requestSubmit === "function") form.requestSubmit(button || undefined);
  else if (button) button.click();
  else form.dispatchEvent(new win.Event("submit", { bubbles: true, cancelable: true }));
  return true;
}
